// ============================================
// WHOIS Lookup Page - Domain WHOIS information
// ============================================

import { useState, useCallback } from "react";
import { GeminiService } from "../../services/geminiService";

interface WhoisData {
  domain: string;
  registrar?: string;
  registrarUrl?: string;
  creationDate?: string;
  expirationDate?: string;
  updatedDate?: string;
  status?: string[];
  nameServers?: string[];
  dnssec?: string;
  registrantName?: string;
  registrantOrg?: string;
  registrantCountry?: string;
  raw?: string;
}

interface WhoisLookupProps {
  geminiService: GeminiService;
  onNavigateToChat?: (domain: string) => void;
}

export function WhoisLookup({ geminiService, onNavigateToChat }: WhoisLookupProps) {
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [whoisData, setWhoisData] = useState<WhoisData | null>(null);
  const [recentLookups, setRecentLookups] = useState<string[]>([]);
  const [aiAnalysis, setAiAnalysis] = useState<string | null>(null);
  const [analyzingAi, setAnalyzingAi] = useState(false);

  const handleLookup = useCallback(async () => {
    if (!domain.trim()) return;

    const cleanDomain = domain.trim().toLowerCase().replace(/^(https?:\/\/)?(www\.)?/, '').split('/')[0];

    setLoading(true);
    setError(null);
    setWhoisData(null);
    setAiAnalysis(null);

    try {
      // Use RDAP (Registration Data Access Protocol) - the modern replacement for WHOIS
      const response = await fetch(`https://rdap.org/domain/${cleanDomain}`);

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('Domain not found or not registered');
        }
        throw new Error(`Lookup failed: ${response.status}`);
      }

      const data = await response.json();

      // Parse RDAP response
      const parsed: WhoisData = {
        domain: cleanDomain,
        nameServers: data.nameservers?.map((ns: { ldhName: string }) => ns.ldhName) || [],
        status: data.status || [],
        dnssec: data.secureDNS?.delegationSigned ? 'signed' : 'unsigned',
      };

      // Extract dates
      for (const event of data.events || []) {
        if (event.eventAction === 'registration') {
          parsed.creationDate = new Date(event.eventDate).toLocaleDateString();
        } else if (event.eventAction === 'expiration') {
          parsed.expirationDate = new Date(event.eventDate).toLocaleDateString();
        } else if (event.eventAction === 'last changed') {
          parsed.updatedDate = new Date(event.eventDate).toLocaleDateString();
        }
      }

      // Extract registrar
      for (const entity of data.entities || []) {
        if (entity.roles?.includes('registrar')) {
          parsed.registrar = entity.vcardArray?.[1]?.find((v: unknown[]) => v[0] === 'fn')?.[3] || entity.handle;
          parsed.registrarUrl = entity.links?.find((l: { rel: string }) => l.rel === 'self')?.href;
        }
        if (entity.roles?.includes('registrant')) {
          const vcard = entity.vcardArray?.[1] || [];
          parsed.registrantName = vcard.find((v: unknown[]) => v[0] === 'fn')?.[3];
          parsed.registrantOrg = vcard.find((v: unknown[]) => v[0] === 'org')?.[3];
          const addr = vcard.find((v: unknown[]) => v[0] === 'adr');
          if (addr && Array.isArray(addr[3])) {
            parsed.registrantCountry = addr[3][6];
          }
        }
      }

      parsed.raw = JSON.stringify(data, null, 2);

      setWhoisData(parsed);

      // Add to recent lookups
      setRecentLookups(prev => {
        const updated = [cleanDomain, ...prev.filter(d => d !== cleanDomain)].slice(0, 10);
        return updated;
      });

    } catch (err) {
      setError(err instanceof Error ? err.message : 'WHOIS lookup failed');
    } finally {
      setLoading(false);
    }
  }, [domain]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleLookup();
    }
  };

  const handleAiAnalysis = useCallback(async () => {
    if (!whoisData) return;

    setAnalyzingAi(true);
    try {
      const prompt = `Analyze this WHOIS data for ${whoisData.domain}:
- Registered: ${whoisData.creationDate || 'Unknown'}
- Expires: ${whoisData.expirationDate || 'Unknown'}
- Registrar: ${whoisData.registrar || 'Unknown'}
- Status: ${whoisData.status?.join(', ') || 'Unknown'}
- Nameservers: ${whoisData.nameServers?.join(', ') || 'Unknown'}

Provide insights about:
1. Domain age and history implications
2. Registration status (is it about to expire?)
3. Any red flags or notable patterns
4. Investment potential based on this data`;

      const analysis = await geminiService.chatMessage([], prompt);
      setAiAnalysis(analysis);
    } catch (err) {
      console.error('AI analysis failed:', err);
    } finally {
      setAnalyzingAi(false);
    }
  }, [whoisData, geminiService]);

  const getStatusColor = (status: string) => {
    const lower = status.toLowerCase();
    if (lower.includes('ok') || lower.includes('active')) return 'text-green-400';
    if (lower.includes('pending') || lower.includes('transfer')) return 'text-yellow-400';
    if (lower.includes('prohibited') || lower.includes('hold')) return 'text-red-400';
    return 'text-green-600';
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 border-b-2 border-green-500">
        <h1 className="text-xl text-green-400 tracking-wider font-bold mb-3">
          WHOIS LOOKUP
        </h1>

        {/* Search bar */}
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Enter domain name (e.g., example.com)"
              className="w-full px-4 py-2 bg-black border-2 border-green-500 text-green-400 font-mono placeholder-green-700 focus:outline-none focus:border-green-400"
              style={{ boxShadow: '0 0 10px rgba(34, 197, 94, 0.2)' }}
            />
          </div>
          <button
            onClick={handleLookup}
            disabled={loading || !domain.trim()}
            className="px-6 py-2 bg-green-500 text-black font-bold tracking-wider hover:bg-green-400 disabled:opacity-50"
          >
            {loading ? 'LOOKING UP...' : 'LOOKUP'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-4">
        {/* Error display */}
        {error && (
          <div className="mb-4 p-3 border-2 border-red-500 bg-red-900/20 text-red-400 text-sm">
            ERROR: {error}
          </div>
        )}

        {/* Recent lookups */}
        {!whoisData && recentLookups.length > 0 && (
          <div className="mb-4 p-3 border border-green-700 bg-green-900/10">
            <span className="text-xs text-green-600 tracking-wider">RECENT:</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {recentLookups.map((d) => (
                <button
                  key={d}
                  onClick={() => {
                    setDomain(d);
                    handleLookup();
                  }}
                  className="px-2 py-1 border border-green-700 text-green-500 text-xs hover:border-green-500"
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* WHOIS Results */}
        {whoisData && (
          <div className="space-y-4">
            {/* Domain header */}
            <div className="p-4 border-2 border-green-500 bg-green-900/10" style={{ boxShadow: '0 0 15px rgba(34, 197, 94, 0.2)' }}>
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-mono text-green-400">{whoisData.domain}</h2>
                  <div className="flex items-center gap-3 mt-2">
                    {whoisData.status?.slice(0, 3).map((s, i) => (
                      <span key={i} className={`text-xs ${getStatusColor(s)}`}>
                        {s.split(' ')[0]}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={handleAiAnalysis}
                    disabled={analyzingAi}
                    className="px-4 py-2 bg-purple-600 text-white font-bold text-sm hover:bg-purple-500 disabled:opacity-50"
                  >
                    {analyzingAi ? 'ANALYZING...' : 'AI INSIGHTS'}
                  </button>
                  {onNavigateToChat && (
                    <button
                      onClick={() => onNavigateToChat(whoisData.domain)}
                      className="px-4 py-2 border border-green-500 text-green-500 font-bold text-sm hover:bg-green-500 hover:text-black"
                    >
                      DISCUSS IN CHAT
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* AI Analysis */}
            {aiAnalysis && (
              <div className="p-4 border border-purple-500 bg-purple-900/10">
                <h3 className="text-sm text-purple-400 font-bold tracking-wider mb-2">AI ANALYSIS</h3>
                <div className="text-green-300 text-sm whitespace-pre-wrap">{aiAnalysis}</div>
              </div>
            )}

            {/* Key details grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 border border-green-700 bg-green-900/10">
                <div className="text-xs text-green-600 tracking-wider mb-1">CREATED</div>
                <div className="text-green-400 font-mono">{whoisData.creationDate || 'Unknown'}</div>
              </div>
              <div className="p-3 border border-green-700 bg-green-900/10">
                <div className="text-xs text-green-600 tracking-wider mb-1">EXPIRES</div>
                <div className="text-green-400 font-mono">{whoisData.expirationDate || 'Unknown'}</div>
              </div>
              <div className="p-3 border border-green-700 bg-green-900/10">
                <div className="text-xs text-green-600 tracking-wider mb-1">UPDATED</div>
                <div className="text-green-400 font-mono">{whoisData.updatedDate || 'Unknown'}</div>
              </div>
              <div className="p-3 border border-green-700 bg-green-900/10">
                <div className="text-xs text-green-600 tracking-wider mb-1">DNSSEC</div>
                <div className={`font-mono ${whoisData.dnssec === 'signed' ? 'text-green-400' : 'text-yellow-500'}`}>
                  {whoisData.dnssec || 'Unknown'}
                </div>
              </div>
            </div>

            {/* Registrar info */}
            {whoisData.registrar && (
              <div className="p-3 border border-green-700 bg-green-900/10">
                <div className="text-xs text-green-600 tracking-wider mb-1">REGISTRAR</div>
                <div className="text-green-400">{whoisData.registrar}</div>
                {whoisData.registrarUrl && (
                  <a
                    href={whoisData.registrarUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-green-600 hover:text-green-400"
                  >
                    {whoisData.registrarUrl}
                  </a>
                )}
              </div>
            )}

            {/* Nameservers */}
            {whoisData.nameServers && whoisData.nameServers.length > 0 && (
              <div className="p-3 border border-green-700 bg-green-900/10">
                <div className="text-xs text-green-600 tracking-wider mb-2">NAMESERVERS</div>
                <div className="grid grid-cols-2 gap-2">
                  {whoisData.nameServers.map((ns, i) => (
                    <div key={i} className="text-green-400 font-mono text-sm">{ns}</div>
                  ))}
                </div>
              </div>
            )}

            {/* Status list */}
            {whoisData.status && whoisData.status.length > 0 && (
              <div className="p-3 border border-green-700 bg-green-900/10">
                <div className="text-xs text-green-600 tracking-wider mb-2">STATUS FLAGS</div>
                <div className="flex flex-wrap gap-2">
                  {whoisData.status.map((s, i) => (
                    <span key={i} className={`px-2 py-1 border border-green-700 text-xs ${getStatusColor(s)}`}>
                      {s}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Raw data toggle */}
            <details className="border border-green-700 bg-green-900/10">
              <summary className="p-3 cursor-pointer text-xs text-green-600 tracking-wider hover:text-green-400">
                RAW RDAP DATA
              </summary>
              <pre className="p-3 text-xs text-green-500 overflow-auto max-h-96 border-t border-green-700">
                {whoisData.raw}
              </pre>
            </details>
          </div>
        )}

        {/* Empty state */}
        {!whoisData && !loading && !error && (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <div className="text-green-700 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <p className="text-green-600 tracking-wider">Enter a domain name to lookup WHOIS information</p>
            <p className="text-green-700 text-sm mt-2">Uses RDAP protocol for accurate, up-to-date data</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default WhoisLookup;
